import { useState, useEffect, useReducer, useMemo, useCallback } from 'react';
import { Link, resolvePath, useNavigate, useParams, useLocation } from 'react-router-dom';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import 'react-contexify/dist/ReactContexify.css';
import { ErrorBoundary } from 'react-error-boundary';
import { CUSTOM_PACKAGES_QUERY, FILES_QUERY } from '../../queries';
import { isIdFile } from '../../helpers/fileKinds';
import Button from '../../components/inputs/Button';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { useOrder } from '../../hooks/useOrder';
import { useAuth } from '../../hooks/useAuth';
import { getProduct } from '../../helpers/products';
import {
  reducer as packageBuilderReducer,
  initialState as packageBuilderInitialState,
  PACKAGE_NAME_CHANGE,
  SET_PACKAGE_NAME_ERROR,
  SET_SAVED_PACKAGE,
  TYPES,
  RESET,
  ADD_VARIABLE,
  LOAD_SAVED_PACKAGE,
} from '../../reducers/packageBuilderReducer';
import {
  reducer as actionReducer,
  initialState as actionInitialState,
  SET_SUCCESS,
  CLEAR_SUCCESS,
  START_ACTION,
} from '../../reducers/actionReducer';
import '../../styles/reactContexifyOverrides.css';
import ErrorText from '../../components/ErrorText';
import PackageNameForm from '../../components/enhancements/PackageNameForm';
import EnhancementsVariablesDropzone, {
  canAddVariable,
} from '../../components/enhancements/EnhancementsVariablesDropzone';
import useNavigationPrompt from '../../hooks/useNavigationPrompt';
import EnhancementsDataDictionary from '../../components/enhancements/EnhancementsDataDictionary';
import NavigationPromptModal from '../../components/builder/NavigationPromptModal';
import InsetLoadingOverlay from '../../components/InsetLoadingOverlay';
import classNames from 'classnames';
import { SAVE_PACKAGE } from '../../mutations';
import ToastNotification from '../../components/ToastNotification';
import ActionModal from '../../components/ActionModal';
import { Item, Menu } from 'react-contexify';
import DeletePackage from '../../components/actions/DeletePackage';
import SavedPackagesList from '../../components/enhancements/SavedPackagesList';

function ErrorFallback({ error, resetErrorBoundary }) {
  return (
    <div className="flex flex-col h-screen w-screen justify-center items-center">
      <div className="mb-6">Uh oh, something went wrong. Let's go back.</div>
      <Button type="button" primary>
        <Link to={-1}>Go back</Link>
      </Button>
    </div>
  );
}

export default function CustomPackageBuilder({ resetBuilder, location: locationProp }) {
  const [state, dispatch] = useReducer(
    packageBuilderReducer,
    packageBuilderInitialState
  );
  const [actionState, dispatchAction] = useReducer(
    actionReducer,
    actionInitialState
  );

  const navigate = useNavigate();
  const client = useApolloClient();
  const location = locationProp || useLocation();
  const { openOrder: openOrderMutation, state: orderState } = useOrder();
  const { user } = useAuth();

  const { fileId } = useParams();
  const [matchedEnhancement, setMatchedEnhancement] = useState(null);

  // Check if we're enhancing an audience
  const audienceData = location.state?.orderDetails;

  const [contextMenuError, setContextMenuError] = useState<
    string | undefined
  >();

  const {
    loading: loadingFiles,
    error: errorFiles,
    data: dataFiles,
  } = useQuery(FILES_QUERY, {
    fetchPolicy: 'cache-and-network',
  });
  const files = dataFiles?.files;

  useEffect(() => {
    // Skip file validation if we're enhancing an audience
    if (audienceData?.audienceId) {
      return;
    }

    if (fileId) {
      function navigateToMyWorkFiles() {
        navigate('../../../mywork/files');
      }
      if (error) {
        navigateToMyWorkFiles();
      }
      if (!loading && files && files?.length) {
        // ATAD-136/ATAD-146: ZIP code and model files have no matched
        // records to use here.
        const foundInFiles = files.find(
          (file) => fileId === file.id && isIdFile(file)
        );
        if (!foundInFiles) {
          navigateToMyWorkFiles();
        }
        setMatchedEnhancement(foundInFiles);
      }
    }
  }, [errorFiles, fileId, loadingFiles, navigate]);

  const [
    savePackage,
    { loading: loadingSavePackage, error: errorSavePackage },
  ] = useMutation(SAVE_PACKAGE, {
    fetchPolicy: 'network-only',
  });

  const handleSavePackageClick = useCallback(
    (packageNameInput: string, callbackFn: () => void) => {
      try {
        savePackage({
          variables: {
            packageId: state.packageId,
            packageName: packageNameInput,
            variablesIncluded: state.variablesIncluded,
          },
          onCompleted: (data) => {
            if (data && data.savePackage) {
              client.refetchQueries({
                include: [CUSTOM_PACKAGES_QUERY],
              });
              dispatch({
                type: SET_SAVED_PACKAGE,
                payload: {
                  packageId: data.savePackage.id,
                  packageName: packageNameInput,
                },
              });
              dispatchAction({
                type: SET_SUCCESS,
                payload: {
                  message: 'Saved package successfully',
                },
              });
              if (typeof callbackFn === 'function') {
                callbackFn();
              }
            }
          },
          onError: (err) => {
            setContextMenuError(err.message);
          },
        });
      } catch (err) {
        setContextMenuError(err.message);
      }
    },
    [state, savePackage]
  );

  const handleSavedPackageClick = useCallback(
    (pkg) => {
      function loadPackage() {
        dispatch({
          type: LOAD_SAVED_PACKAGE,
          payload: {
            packageId: pkg.id,
            packageName: pkg.name,
            variablesIncluded: pkg.variablesIncluded || [],
          },
        });
      }
      if (state.hasStateChanged) {
        setShowLoadPrompt(true);
        setConfirmLoadPrompt({
          loadFn: () => {
            dispatch({ type: RESET });
            loadPackage();
          },
          label: 'Load',
        });
        return;
      }
      dispatch({ type: RESET });
      loadPackage();
    },
    [state]
  );

  const handlePurchaseAudienceEnhancement = useCallback(async () => {
    if (!audienceData?.audienceId || !state.packageId) {
      setContextMenuError('Missing required data to create order');
      return;
    }

    try {
      const product = getProduct('audience-enhancement', user?.plan);

      const result = await openOrderMutation({
        variables: {
          products: [
            {
              ...product, // includes id, priceId, price
              quantity: audienceData.recordsIncluded,
            },
          ],
          orderType: 'audience-enhancement',
          orderDetails: {
            audience: audienceData.audienceId,
            audienceName: audienceData.audienceName,
            packageName: state.packageName,
            recordsIncluded: audienceData.recordsIncluded,
            packageDefinition: state.packageId,
          },
        },
      });

      // Navigate to order summary page
      if (result.data?.openOrder?.id) {
        navigate(`../audience/${result.data.openOrder.id}`, {
          state: {
            orderDetails: {
              orderId: result.data.openOrder.id,
              coreProductName: 'audience-enhancement',
              audienceId: audienceData.audienceId,
              audienceName: audienceData.audienceName,
              packageName: state.packageName,
              recordsIncluded: audienceData.recordsIncluded,
              packageDefinition: state.packageId,
            },
          },
        });
      }
    } catch (err) {
      setContextMenuError(err.message);
    }
  }, [audienceData, state, openOrderMutation, navigate, user?.plan]);

  useEffect(() => {
    if (orderState.errorOpenOrder) {
      setContextMenuError(orderState.errorOpenOrder.message || 'Failed to create order');
    }
  }, [orderState.errorOpenOrder]);

  useEffect(() => {
    if (actionState.success) {
      setTimeout(
        () =>
          dispatchAction({
            type: CLEAR_SUCCESS,
          }),
        5000
      );
    }
  }, [actionState.success]);

  useEffect(() => {
    if (contextMenuError) {
      setTimeout(() => setContextMenuError(null), 5000);
    }
  }, [contextMenuError]);

  const error = useMemo(
    () => errorFiles?.message || errorSavePackage?.message,
    [errorFiles, errorSavePackage]
  );

  const loading = useMemo(
    () => loadingFiles || loadingSavePackage,
    [loadingSavePackage, loadingFiles]
  );

  const shouldBlockNavigation = useMemo(
    () => !!(state.hasStateChanged || loading),
    [state.hasStateChanged, loading]
  );

  const canSave = useMemo(() => {
    if (state.hasStateChanged && !state.packageNameError) {
      return true;
    }
    if (
      !state.isSavedPackage &&
      state.variablesIncluded?.length > 0 &&
      !state.packageNameError
    ) {
      return true;
    }
    return false;
  }, [
    state.hasStateChanged,
    state.isSavedPackage,
    state.variablesIncluded?.length,
    state.packageNameError,
  ]);

  const canSelectFileToEnhance = useMemo(
    () =>
      // For audiences, allow proceeding if package is saved with no changes and has variables
      audienceData?.audienceId
        ? state.isSavedPackage &&
          !state.hasStateChanged &&
          state.variablesIncluded?.length > 0
        : (state.isSavedPackage &&
            !state.hasStateChanged &&
            state.variablesIncluded?.length > 0) ||
          (!state.isSavedPackage && !state.hasStateChanged && !matchedEnhancement),
    [
      audienceData?.audienceId,
      state.isSavedPackage,
      state.hasStateChanged,
      state.variablesIncluded?.length,
      matchedEnhancement,
    ]
  );

  const [showPrompt, confirmNavigation, cancelNavigation] = useNavigationPrompt(
    shouldBlockNavigation,
    [
      {
        path: audienceData?.audienceId
          ? resolvePath('./audience', location.pathname)?.pathname
          : resolvePath('../order', location.pathname)?.pathname,
        end: false,
      },
    ]
  );

  const [showLoadPrompt, setShowLoadPrompt] = useState<boolean>(false);
  const [confirmLoadPrompt, setConfirmLoadPrompt] = useState<{
    loadFn: () => void;
    label: string;
  } | null>(null);

  const resetPackageFn = useCallback(() => {
    try {
      window.history.replaceState({}, document.title);
    } catch (err) {
      console.error('could not clear state');
    }
    resetBuilder();
  }, [resetBuilder]);

  const handleAttributeClick = useCallback(
    ({ props }) => {
      const { id, key } = props;

      if (!canAddVariable(state.variablesIncluded, id)) {
        setContextMenuError('Attribute has already been added.');
        return;
      }

      dispatch({
        type: ADD_VARIABLE,
        payload: {
          id,
          key,
        },
      });
    },
    [state]
  );

  function handleDeleteClick({ props }) {
    const { id, name } = props;

    dispatchAction({
      type: START_ACTION,
      payload: {
        name: 'Delete Package',
        component: (
          <DeletePackage
            dispatchAction={dispatchAction}
            packageName={name}
            packageId={id}
          />
        ),
      },
    });
  }

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => resetPackageFn()}
    >
      <DndProvider backend={HTML5Backend}>
        <div className="relative flex flex-grow max-w-full max-h-full overflow-auto">
          {loading && (
            <>{loading && <InsetLoadingOverlay message="Loading..." />}</>
          )}
          <div className="flex flex-col flex-grow-0 w-72 bg-gray-100 py-3 px-5 rounded-tr-md max-h-full overflow-auto resize-x">
            <div>
              <h4>My Templates</h4>
            </div>
            <SavedPackagesList
              onSavedPackageClick={handleSavedPackageClick}
              currentPackageName={state.packageName}
            />
            <div className="border-b w-full border-black my-5"></div>
            <div>
              <h4 className="mb-3">Data Dictionary</h4>
            </div>
            <div className="flex">
              <EnhancementsDataDictionary />
            </div>
          </div>
          <div className="flex flex-col flex-grow p-3 pl-5 overflow-auto">
            <div className="flex flex-row items-center">
              <h3 className="mr-5">Build Your Custom Enhancement Package</h3>
              <button
                className="font-bold text-blue hover:text-primary mb-4 text-sm"
                type="button"
                onClick={() => {
                  if (state.hasStateChanged) {
                    setShowLoadPrompt(true);
                    setConfirmLoadPrompt({
                      loadFn: resetPackageFn,
                      label: 'Reset',
                    });
                    return;
                  }
                  resetPackageFn();
                }}
              >
                Reset
              </button>
            </div>
            <div className="flex flex-row pt-2 pb-5 justify-between">
              <div className="flex flex-col">
                <PackageNameForm
                  onBlur={(value) =>
                    state.packageName !== value &&
                    dispatch({
                      type: PACKAGE_NAME_CHANGE,
                      payload: {
                        value,
                      },
                    })
                  }
                  onSubmit={handleSavePackageClick}
                  initialValue={state.packageName}
                  canSave={canSave}
                  isSavedPackage={state.isSavedPackage}
                  hasStateChanged={state.hasStateChanged}
                  setNameError={(val) =>
                    dispatch({
                      type: SET_PACKAGE_NAME_ERROR,
                      payload: {
                        packageNameError: val,
                      },
                    })
                  }
                />
                {state.packageNameError && (
                  <ErrorText
                    className="-mb-7 mt-1"
                    message="A package with that name already exists. Please use a different name."
                  />
                )}
              </div>
              {matchedEnhancement && (
                <div className="flex flex-col">
                  <p>
                    File to enhance:{' '}
                    <span className="font-bold">{matchedEnhancement.name}</span>
                  </p>
                  <p>
                    Records matched:{' '}
                    <span className="font-bold">
                      {matchedEnhancement?.numMatchedRecords?.toLocaleString()}
                    </span>
                  </p>
                </div>
              )}
            </div>
            <div className="flex flex-row flex-grow space">
              <div className="flex flex-col flex-grow pr-3 py-2 gap-2">
                <div className="flex flex-row justify-between">
                  <h4 className="mb-0 pr-4">Selected Variables</h4>
                </div>
                <EnhancementsVariablesDropzone
                  dropzoneMessage="Drag and drop desired elements from the left to customize
                    your bundle"
                  state={state}
                  dispatch={dispatch}
                />
              </div>
              <div className="flex flex-col p-2">
                <div className="mb-2">
                  <h4 className="mb-0">My Package</h4>
                </div>
                <div className="flex flex-col bg-gray-100 rounded-half p-4 gap-4">
                  <div className="bg-white rounded-half py-6 px-4 text-center">
                    <p className="text-xl font-bold">
                      {state.variablesIncluded?.length || 0}
                    </p>
                    <p className="text-gray-400">Variables</p>
                  </div>
                </div>

                <div className="pt-5">
                  {audienceData?.audienceId ? (
                    <Button
                      className="w-full"
                      type="button"
                      primary
                      disabled={!canSelectFileToEnhance || orderState.loadingOpenOrder}
                      onClick={handlePurchaseAudienceEnhancement}
                    >
                      {orderState.loadingOpenOrder ? 'Creating Order...' : 'Purchase Enhancement'}
                    </Button>
                  ) : (
                    <Link
                      to={
                        matchedEnhancement
                          ? '../../order'
                          : '../../enhancements/fileselection'
                      }
                      state={
                        matchedEnhancement
                          ? {
                              orderDetails: {
                                coreProductName: 'customEnhancement',
                                fileId,
                                fileName: matchedEnhancement.name,
                                packageName: state.packageName,
                                matchedRecords:
                                  matchedEnhancement.numMatchedRecords,
                                packageDefinition: state.packageId,
                              },
                            }
                          : null
                      }
                      className={classNames({
                        'pointer-events-none': !canSelectFileToEnhance,
                      })}
                    >
                      <Button
                        className="w-full"
                        type="button"
                        primary
                        disabled={!canSelectFileToEnhance}
                      >
                        {matchedEnhancement
                          ? 'Purchase Enhancement'
                          : 'Select File to Enhance'}
                      </Button>
                    </Link>
                  )}
                </div>
                <ErrorText message={error} className="pt-5 w-72" />
              </div>
            </div>
          </div>
        </div>
        {actionState.inProgress && (
          <ActionModal
            actionState={actionState}
            dispatchAction={dispatchAction}
            show={actionState.inProgress}
          />
        )}
        <ToastNotification
          message={actionState.success}
          success={true}
          show={actionState.success}
        />
        <ToastNotification
          message={contextMenuError}
          error={true}
          show={Boolean(contextMenuError)}
        />

        <Menu id={TYPES.VARIABLE}>
          <Item onClick={handleAttributeClick}>Include</Item>
        </Menu>
        <Menu id="delete-package">
          <Item onClick={handleDeleteClick}>Delete</Item>
        </Menu>
        {showPrompt && (
          <NavigationPromptModal
            show={showPrompt}
            onConfirm={confirmNavigation}
            onCancel={cancelNavigation}
            directions={
              <>
                <p>You have unsaved changes.</p>
                <p>If you navigate away your unsaved changes will be lost.</p>
                <p>
                  To save your package click "Return to save" and then "Save
                  Package".
                </p>
              </>
            }
          />
        )}
        {showLoadPrompt && (
          <NavigationPromptModal
            show={showLoadPrompt}
            onConfirm={() => {
              setShowLoadPrompt(false);
              confirmLoadPrompt?.loadFn();
            }}
            onCancel={() => {
              setShowLoadPrompt(false);
              setConfirmLoadPrompt(null);
            }}
            confirmLabel={confirmLoadPrompt?.label}
            directions={
              <>
                <p>You have unsaved changes.</p>
                <p>If you navigate away your unsaved changes will be lost.</p>
                <p>
                  To save your package click "Return to save" and then "Save
                  Package".
                </p>
              </>
            }
          />
        )}
      </DndProvider>
    </ErrorBoundary>
  );
}
